import { CheckIcon, DeleteIcon, DownloadIcon } from "@chakra-ui/icons";
import { FC } from "react";

import { ApiUploadtasksListRequest, UploadTask } from "@nens/brostar/openapi-client";
import { ColumnDefinition } from "@nens/chakra/components";

import { getApi } from "@/api/api-configuration";
import { getSearchKey } from "@/components/searchbox";
import { TableOverview } from "@/components/table-overview";
import {
  renderDefault,
  renderEnum,
  renderLog,
  renderReference,
  renderStatus,
} from "@/helpers/table-render-functions";
import { useQueryPagination } from "@/hooks/use-pagination";
import { KEY_ADDON_MAP, useRouterTableData } from "@/hooks/use-router-table-data";
import {
  useInvalidateUploadTaskQueries,
  useUploadTaskListQuery,
} from "@/queries/query-upload-task";

import { UploadTaskFilters } from "./components/upload-task-filters";
import { registrationTypeOptions, requestTypeOptions, uploadTaskInformation } from "./types";

const columnDefinitions: ColumnDefinition<UploadTask>[] = [
  {
    title: "Status",
    renderFunction: renderStatus,
    width: "50px",
  },
  {
    title: "Domein",
    renderFunction: (row) => renderDefault(row.broDomain),
    width: "70px",
  },
  {
    title: "Registratie",
    renderFunction: (row) => renderEnum(row.registrationType, registrationTypeOptions),
    width: "150px",
  },
  {
    title: "Soort",
    renderFunction: (row) => renderEnum(row.requestType, requestTypeOptions),
    width: "100px",
  },
  {
    title: "Referentie",
    renderFunction: renderReference,
    width: "300px",
  },
  {
    title: "Datum",
    renderFunction: (row) => renderDefault(row.created),
    width: "170px",
  },
  {
    title: "Log",
    renderFunction: renderLog,
    width: "auto",
  },
];

const actionShouldBeDisabled = ({ status }: UploadTask) =>
  status === "COMPLETED" || status === "UNFINISHED";

const UploadTaskTable: FC = () => {
  const api = getApi();
  const {
    page,
    setPage,
    searchField: _searchField,
    searchString,
    setSearchField,
    setSearchString,
  } = useRouterTableData({ keyAddon: KEY_ADDON_MAP.UPLOAD_TASK });
  const searchField =
    (_searchField as keyof ApiUploadtasksListRequest | undefined) ||
    getSearchKey(uploadTaskInformation[0]);
  const invalidateUploadTaskListQuery = useInvalidateUploadTaskQueries();

  const queryParams: ApiUploadtasksListRequest = { [searchField]: searchString, page };
  const { data: uploadTaskList, isLoading, isRefetching } = useUploadTaskListQuery(queryParams);
  const paginationProps = useQueryPagination({
    queryResponse: uploadTaskList,
    page,
    setPage,
  });
  const removeUploadTask = async (uuid: string) => {
    await api.apiUploadtasksDelete({ uuid });
    invalidateUploadTaskListQuery();
  };

  return (
    <>
      <UploadTaskFilters
        isLoadingOrRefetching={isLoading || isRefetching}
        {...{ searchField, searchString, setSearchField, setSearchString }}
      />
      <TableOverview
        rowActions={[
          {
            label: "Check status",
            icon: <CheckIcon boxSize={4} />,
            action: (row: UploadTask) => {
              api.apiUploadtasksCheckStatusRaw({ uuid: row.uuid ?? "", data: row });
            },
            disabled: (row: UploadTask) => row.status !== "UNFINISHED",
          },
          {
            label: "Verwijder",
            icon: <DeleteIcon boxSize={4} />,
            action: (row: UploadTask) => row.uuid && removeUploadTask(row.uuid),
            disabled: actionShouldBeDisabled,
          },
          // FUTURE: Reintroduce the editing of upload tasks
          // {
          //   label: "Pas aan",
          //   icon: <EditIcon boxSize={4} />,
          //   action: (row: UploadTask) => {
          //     const mappedValue = mapRegistrationType(row.registrationType);
          //     postMessage(`/${row.broDomain?.toLowerCase()}/${mappedValue}uploadTask/${row.uuid}`);
          //   },
          //   disabled: actionShouldBeDisabled,
          // },
          {
            label: "Download XML",
            icon: <DownloadIcon boxSize={4} />,
            action: async (row: UploadTask) => {
              try {
                // Call your API to get XML content
                const xmlContent = await api.apiUploadtasksReadXml({
                  uuid: row.uuid ?? "",
                });

                // Convert XML content to Blob
                const blob = new Blob([xmlContent], { type: "application/xml" });

                // Create a download link
                const url = URL.createObjectURL(blob);
                const link = document.createElement("a");
                link.href = url;

                // Set filename: {datetime}_{registrationType}.xml
                const datetime = new Date().toISOString().replace(/[:.]/g, "-");
                link.download = `${datetime}_${row.registrationType}.xml`;

                // Trigger download
                document.body.appendChild(link);
                link.click();

                // Cleanup
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
              } catch (error) {
                console.error("Failed to download XML:", error);
              }
            },
          },
        ]}
        isLoading={isLoading}
        columnDefinitions={columnDefinitions}
        tableData={uploadTaskList?.results || []}
        paginationProps={paginationProps}
      />
    </>
  );
};

export { UploadTaskTable };
